package com.evoapps.evoshift.ui.workplaces

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Print
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.intl.Locale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.domain.model.TimeRoundingMode
import com.evoapps.evoshift.domain.model.TimesheetHoursSource
import com.evoapps.evoshift.domain.model.TimesheetReportSettings
import com.evoapps.evoshift.domain.model.WeekdayFormat
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.finance.formatDecimal
import com.evoapps.evoshift.ui.reports.ReportHtml
import com.evoapps.evoshift.util.usesDecimalComma
import com.evoapps.evoshift.widgets.DataRow
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoRadioGroup
import com.evoapps.evoshift.widgets.EvoSegmented
import com.evoapps.evoshift.widgets.EvoSwitchRow
import com.evoapps.evoshift.widgets.ProfileSection
import evoshift.composeapp.generated.resources.*
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

const val REPORT_SETTINGS_EXAMPLE_TAG = "settings_reports_example"
const val REPORT_SETTINGS_PREVIEW_TAG = "settings_reports_preview"

/**
 * SH-258: the sample the settings show: a Tuesday clocked 07:58–16:12 against the plan
 * 08:00–16:00, as the working-time record would print it with the chosen settings.
 */
object TimesheetSample {
    val plannedStart = LocalDateTime(2026, 2, 3, 8, 0)
    val plannedEnd = LocalDateTime(2026, 2, 3, 16, 0)
    val clockedStart = LocalDateTime(2026, 2, 3, 7, 58)
    val clockedEnd = LocalDateTime(2026, 2, 3, 16, 12)

    fun span(start: LocalDateTime, end: LocalDateTime): String = "${hm(start)}–${hm(end)}"

    /** "07:58–16:12" and how the record shows it ("08:00–16:15" at 15 minutes to the nearest). */
    fun example(o: TimesheetReportSettings): Pair<String, String> =
        span(clockedStart, clockedEnd) to o.round(clockedStart, clockedEnd).let { (s, e) -> span(s, e) }

    /** The sample row's plan, clocked times (null: the column is not printed) and hours. */
    data class Row(val plan: String?, val clocked: String?, val hours: Double)

    fun row(o: TimesheetReportSettings): Row {
        val plan = o.round(plannedStart, plannedEnd)
        val clocked = o.round(clockedStart, clockedEnd)
        val counted = if (o.hoursSource == TimesheetHoursSource.PLAN) plan else clocked
        return Row(
            plan = span(plan.first, plan.second).takeIf { o.hoursSource != TimesheetHoursSource.ACTUAL },
            clocked = span(clocked.first, clocked.second).takeIf { o.hoursSource != TimesheetHoursSource.PLAN },
            hours = (counted.second.toInstant(TimeZone.UTC) - counted.first.toInstant(TimeZone.UTC)).inWholeMinutes / 60.0
        )
    }

    private fun hm(t: LocalDateTime) = "${t.hour.toString().padStart(2, '0')}:${t.minute.toString().padStart(2, '0')}"
}

private fun hoursSourceTitle(source: TimesheetHoursSource): StringResource = when (source) {
    TimesheetHoursSource.PLAN -> Res.string.settings_timesheet_hours_plan
    TimesheetHoursSource.ACTUAL -> Res.string.settings_timesheet_hours_actual
    TimesheetHoursSource.COMBINED -> Res.string.settings_timesheet_hours_combined
}

private fun hoursSourceHint(source: TimesheetHoursSource): StringResource = when (source) {
    TimesheetHoursSource.PLAN -> Res.string.settings_timesheet_hours_plan_hint
    TimesheetHoursSource.ACTUAL -> Res.string.settings_timesheet_hours_actual_hint
    TimesheetHoursSource.COMBINED -> Res.string.settings_timesheet_hours_combined_hint
}

private fun directionTitle(mode: TimeRoundingMode): StringResource = when (mode) {
    TimeRoundingMode.NEAREST -> Res.string.settings_timesheet_direction_nearest
    TimeRoundingMode.START_UP_END_DOWN -> Res.string.settings_timesheet_direction_up_down
    TimeRoundingMode.START_DOWN_END_UP -> Res.string.settings_timesheet_direction_down_up
}

private fun directionHint(mode: TimeRoundingMode): StringResource = when (mode) {
    TimeRoundingMode.NEAREST -> Res.string.settings_timesheet_direction_nearest_hint
    TimeRoundingMode.START_UP_END_DOWN -> Res.string.settings_timesheet_direction_up_down_hint
    TimeRoundingMode.START_DOWN_END_UP -> Res.string.settings_timesheet_direction_down_up_hint
}

private fun directionInSentence(mode: TimeRoundingMode): StringResource = when (mode) {
    TimeRoundingMode.NEAREST -> Res.string.report_rounding_nearest
    TimeRoundingMode.START_UP_END_DOWN -> Res.string.report_rounding_start_up_end_down
    TimeRoundingMode.START_DOWN_END_UP -> Res.string.report_rounding_start_down_end_up
}

/** "Do 15 minut, wejście w górę, wyjście w dół" or "Bez zaokrąglania". */
@Composable
fun roundingSummary(o: TimesheetReportSettings): String =
    if (!o.rounds) stringResource(Res.string.settings_timesheet_rounding_off)
    else stringResource(Res.string.settings_timesheet_rounding_to, o.roundingMinutes) + ", " + stringResource(directionInSentence(o.roundingMode))

@Composable
private fun columnLabel(column: ReportHtml.Column): String = when (column) {
    ReportHtml.Column.DATE -> stringResource(Res.string.report_col_date)
    ReportHtml.Column.DAY_SHORT, ReportHtml.Column.DAY_FULL -> stringResource(Res.string.report_col_day)
    ReportHtml.Column.PLAN -> stringResource(Res.string.report_col_planned)
    ReportHtml.Column.CLOCKED -> stringResource(Res.string.report_col_clocked)
    ReportHtml.Column.HOURS -> stringResource(Res.string.report_col_hours)
    ReportHtml.Column.NIGHT -> stringResource(Res.string.report_col_night)
    ReportHtml.Column.NOTES -> stringResource(Res.string.report_col_notes)
    ReportHtml.Column.SIGNATURE -> stringResource(Res.string.report_col_manager_signature)
}

/** SH-258: the "Raporty" section of the workplace settings: the working-time record's choices at a glance. */
@Composable
fun ReportSettingsSection(
    settings: TimesheetReportSettings,
    onEdit: (() -> Unit)?,
    /** SH-269: above the choices (where the values come from: the group or the workplace). */
    header: @Composable () -> Unit = {}
) {
    ProfileSection(
        title = stringResource(Res.string.settings_reports_title),
        icon = Icons.Default.Print,
        onEdit = onEdit,
        help = stringResource(Res.string.settings_reports_hint)
    ) {
        header()
        Text(
            stringResource(Res.string.settings_timesheet),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = DeepNavy,
            modifier = Modifier.padding(bottom = 4.dp)
        )
        DataRow(stringResource(Res.string.settings_timesheet_hours), stringResource(hoursSourceTitle(settings.hoursSource)))
        DataRow(stringResource(Res.string.settings_timesheet_rounding), roundingSummary(settings))
        val columns = ReportHtml.columns(settings).map { columnLabel(it) }
        DataRow(stringResource(Res.string.settings_timesheet_columns), columns.joinToString(", "))
        if (settings.rounds) {
            val (from, to) = TimesheetSample.example(settings)
            Text(
                stringResource(Res.string.settings_timesheet_example, from, to),
                style = MaterialTheme.typography.bodySmall,
                color = DeepNavy.copy(alpha = 0.7f),
                modifier = Modifier.padding(top = 4.dp).testTag(REPORT_SETTINGS_EXAMPLE_TAG)
            )
        }
    }
}

/** SH-258: the working-time record's settings, with the sample row as it would print. */
@Composable
fun TimesheetReportDialog(initial: TimesheetReportSettings, onDismiss: () -> Unit, onSave: (TimesheetReportSettings) -> Unit) {
    var o by remember { mutableStateOf(initial) }
    EvoDialog(
        title = stringResource(Res.string.settings_timesheet),
        icon = Icons.Default.Print,
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.save), { onSave(o) })
    ) {
        DialogSection(stringResource(Res.string.settings_timesheet_hours)) {
            EvoRadioGroup(
                options = TimesheetHoursSource.entries.toList(),
                selected = o.hoursSource,
                onSelect = { o = o.copy(hoursSource = it) },
                optionLabel = { stringResource(hoursSourceTitle(it)) },
                description = { stringResource(hoursSourceHint(it)) }
            )
        }
        DialogSection(stringResource(Res.string.settings_timesheet_rounding)) {
            Text(stringResource(Res.string.settings_timesheet_rounding_minutes), color = Color.White, style = MaterialTheme.typography.bodyMedium)
            EvoSegmented(
                options = TimesheetReportSettings.ROUNDING_CHOICES,
                selected = o.roundingMinutes,
                onSelect = { o = o.copy(roundingMinutes = it) },
                optionLabel = { if (it == 0) stringResource(Res.string.settings_timesheet_rounding_none) else it.toString() }
            )
            if (o.rounds) {
                EvoRadioGroup(
                    options = TimeRoundingMode.entries.toList(),
                    selected = o.roundingMode,
                    onSelect = { o = o.copy(roundingMode = it) },
                    optionLabel = { stringResource(directionTitle(it)) },
                    description = { stringResource(directionHint(it)) }
                )
                val (from, to) = TimesheetSample.example(o)
                Text(
                    stringResource(Res.string.settings_timesheet_example, from, to),
                    color = Color.White,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold
                )
            }
            Text(
                stringResource(Res.string.settings_timesheet_rounding_report_only),
                color = Color.White.copy(alpha = 0.7f),
                style = MaterialTheme.typography.bodySmall
            )
        }
        DialogSection(stringResource(Res.string.settings_timesheet_columns)) {
            EvoSwitchRow(
                checked = o.showRemarks,
                onCheckedChange = { o = o.copy(showRemarks = it) },
                label = stringResource(Res.string.settings_timesheet_remarks),
                supporting = stringResource(Res.string.settings_timesheet_remarks_hint)
            )
            EvoSwitchRow(
                checked = o.showManagerSignature,
                onCheckedChange = { o = o.copy(showManagerSignature = it) },
                label = stringResource(Res.string.settings_timesheet_signature),
                supporting = stringResource(Res.string.settings_timesheet_signature_hint)
            )
            Text(stringResource(Res.string.settings_timesheet_weekday), color = Color.White, style = MaterialTheme.typography.bodyMedium)
            EvoSegmented(
                options = WeekdayFormat.entries.toList(),
                selected = o.weekday,
                onSelect = { o = o.copy(weekday = it) },
                optionLabel = {
                    when (it) {
                        WeekdayFormat.NONE -> stringResource(Res.string.settings_timesheet_weekday_none)
                        WeekdayFormat.SHORT -> stringResource(Res.string.day_tue_short)
                        WeekdayFormat.FULL -> stringResource(Res.string.day_tuesday)
                    }
                }
            )
        }
        DialogSection(stringResource(Res.string.settings_timesheet_preview)) {
            TimesheetPreview(o)
        }
    }
}

/** The record's header and the sample day as printed: the columns in their printed proportions. */
@Composable
private fun TimesheetPreview(o: TimesheetReportSettings) {
    val columns = ReportHtml.columns(o)
    val widths = ReportHtml.columnWidthsMm(columns)
    val sample = TimesheetSample.row(o)
    val decimalComma = usesDecimalComma(Locale.current.language)
    val hours = if (decimalComma) formatDecimal(sample.hours, 2, ' ', ',') else formatDecimal(sample.hours, 2)
    val day = when (o.weekday) {
        WeekdayFormat.FULL -> stringResource(Res.string.day_tuesday)
        else -> stringResource(Res.string.day_tue_short)
    }
    val grid = Color(0xFF8A8A8A)
    Column(
        Modifier.fillMaxWidth()
            .background(Color.White, RoundedCornerShape(6.dp))
            .border(0.5.dp, grid, RoundedCornerShape(6.dp))
            .padding(8.dp)
            .testTag(REPORT_SETTINGS_PREVIEW_TAG)
    ) {
        @Composable
        fun line(cells: List<String>, header: Boolean) {
            Row(Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                columns.forEachIndexed { i, column ->
                    val number = column == ReportHtml.Column.HOURS || column == ReportHtml.Column.NIGHT
                    Text(
                        cells[i],
                        modifier = Modifier.weight(widths[i].toFloat()).fillMaxHeight()
                            .background(if (header) DeepNavy else Color.White)
                            .border(0.5.dp, grid)
                            .padding(horizontal = 2.dp, vertical = 3.dp),
                        color = if (header) Color.White else DeepNavy,
                        fontSize = 9.sp,
                        lineHeight = 11.sp,
                        fontWeight = if (header) FontWeight.Bold else FontWeight.Normal,
                        textAlign = if (number) TextAlign.End else TextAlign.Start,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
        line(columns.map { columnLabel(it) }, header = true)
        line(columns.map { column ->
            when (column) {
                ReportHtml.Column.DATE -> "03.02"
                ReportHtml.Column.DAY_SHORT, ReportHtml.Column.DAY_FULL -> day
                ReportHtml.Column.PLAN -> sample.plan.orEmpty()
                ReportHtml.Column.CLOCKED -> sample.clocked.orEmpty()
                ReportHtml.Column.HOURS -> hours
                ReportHtml.Column.NIGHT, ReportHtml.Column.NOTES, ReportHtml.Column.SIGNATURE -> ""
            }
        }, header = false)
    }
}
